import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useIsBrandAdmin } from '../lib/brandAdmin'
import { supabase } from '../lib/supabase'
import { Plus, Search, X, Truck, Trash2, AlertTriangle, Copy, Check, Download, ChevronDown } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { exportToCsv } from '../lib/exportCsv'
import { loadOrderStates, shipmentBaseStatus, SHIPMENT_STATUS_OPTIONS, DISPLAY } from '../lib/orderStatus'
import HelpLink from '../components/HelpLink'

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false)
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px', color: copied ? 'var(--success)' : 'var(--text-3)', verticalAlign: 'middle' }}>
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  )
}

// Planned → Shipped (or Cancelled); Landed is worked out from the inventory uploads (lib/orderStatus)
const STATUSES = ['planned', 'shipped', 'landed', 'cancelled']
const ACTIVE_STATUSES = ['planned', 'shipped']

const EMPTY = {
  po_id: '', shipment_number: '', supplier_name: '', carrier: '', tracking_number: '',
  expected_ship_date: '', actual_ship_date: '', expected_arrival: '',
  actual_arrival: '', receipt_date: '',
  status: 'scheduled', notes: '',
  is_direct_ship: false, ship_to_location_id: '', ship_to_freeform: ''
}

function SortTh({ col, label, sortCol, sortDir, onSort, style }) {
  const active = sortCol === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => onSort(col)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <span style={{ fontSize: '10px', color: active ? 'var(--accent)' : 'var(--text-3)', lineHeight: 1 }}>
          {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  )
}

export default function Shipments() {
  const { activeBrand } = useAuth()
  const isBrandAdmin = useIsBrandAdmin()
  const navigate = useNavigate()
  const [shipments, setShipments] = useState([])
  const [shipmentOrdered, setShipmentOrdered] = useState({})
  const [shipmentShipped, setShipmentShipped] = useState({})
  const [shipmentReceived, setShipmentReceived] = useState({})
  const [shipmentLines, setShipmentLines] = useState({})
  const [pos, setPOs] = useState([])
  const [locations, setLocations] = useState([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(ACTIVE_STATUSES)
  const [shipState, setShipState] = useState({})
  const stateOf = s => shipState[s.id] || shipmentBaseStatus(s.status)
  const [statusOpen, setStatusOpen] = useState(false)
  const statusRef = useRef(null)
  const [sortCol, setSortCol] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formError, setFormError] = useState('')
  const [noPOWarning, setNoPOWarning] = useState(false)
  const [nextShipNum, setNextShipNum] = useState('SHIP-001')

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  useEffect(() => {
    function onClickOutside(e) {
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  function toggleStatus(st) {
    setStatusFilter(prev => prev.includes(st) ? prev.filter(x => x !== st) : [...prev, st])
  }

  async function load() {
    setLoading(true)
    const [{ data: shipData }, { data: poData }, { data: locData }, { data: lineData }] = await Promise.all([
      supabase.from('shipments').select('*, purchase_orders(po_number, id), ship_to_loc:ship_to_location_id(name, type)').eq('brand_id', activeBrand.id).is('deleted_at', null).order('created_at', { ascending: false }),
      supabase.from('purchase_orders').select('id, po_number').eq('brand_id', activeBrand.id).is('deleted_at', null).order('po_number'),
      supabase.from('locations').select('id, name, type').eq('brand_id', activeBrand.id).eq('is_active', true).order('name'),
      supabase.from('shipment_line_items').select('shipment_id, quantity_ordered, quantity_shipped, quantity_received, products(sku, name)')
    ])
    setShipments(shipData || [])
    try { setShipState((await loadOrderStates(activeBrand.id)).shipmentState) } catch (e) { /* fall back to stored status */ }
    setPOs(poData || [])
    setLocations(locData || [])

    const ordered = {}, shipped = {}, received = {}, lines = {}
    ;(lineData || []).forEach(l => {
      ordered[l.shipment_id] = (ordered[l.shipment_id] || 0) + Number(l.quantity_ordered || 0)
      shipped[l.shipment_id] = (shipped[l.shipment_id] || 0) + Number(l.quantity_shipped || 0)
      received[l.shipment_id] = (received[l.shipment_id] || 0) + Number(l.quantity_received || 0)
      if (!lines[l.shipment_id]) lines[l.shipment_id] = []
      lines[l.shipment_id].push({ sku: l.products?.sku, name: l.products?.name, qty: l.quantity_ordered })
    })
    setShipmentOrdered(ordered)
    setShipmentShipped(shipped)
    setShipmentReceived(received)
    setShipmentLines(lines)

    const nums = (shipData || [])
      .map(s => s.shipment_number)
      .filter(n => n && n.startsWith('SHIP-'))
      .map(n => parseInt(n.replace('SHIP-', '')) || 0)
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1
    setNextShipNum(`SHIP-${String(next).padStart(3, '0')}`)
    setLoading(false)
  }

  function openNew() {
    setForm({ ...EMPTY, shipment_number: nextShipNum })
    setFormError('')
    setNoPOWarning(false)
    setModal(true)
  }

  async function handleCreate() {
    if (!form.po_id && !noPOWarning) {
      setNoPOWarning(true)
      return
    }
    setFormError('')
    setSaving(true)

    const { data: dup } = await supabase.from('shipments')
      .select('id').eq('brand_id', activeBrand.id)
      .eq('shipment_number', form.shipment_number).is('deleted_at', null)
    if (dup && dup.length > 0) {
      setFormError('A shipment with this number already exists.')
      setSaving(false)
      return
    }

    const payload = {
      brand_id: activeBrand.id,
      po_id: form.po_id || null,
      shipment_number: form.shipment_number,
      supplier_name: form.supplier_name || null,
      carrier: form.carrier || null,
      tracking_number: form.tracking_number || null,
      expected_ship_date: form.expected_ship_date || null,
      actual_ship_date: form.actual_ship_date || null,
      expected_arrival: form.expected_arrival || null,
      actual_arrival: form.actual_arrival || null,
      receipt_date: form.receipt_date || null,
      status: form.status,
      notes: form.notes || null,
      is_direct_ship: form.is_direct_ship,
      ship_to_location_id: !form.is_direct_ship && form.ship_to_location_id ? form.ship_to_location_id : null,
      ship_to_freeform: form.is_direct_ship ? form.ship_to_freeform : null,
    }

    const { data: newShipment, error } = await supabase.from('shipments').insert(payload).select().single()
    if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }

    setSaving(false)
    setModal(false)
    load()

    if (newShipment) navigate(`/shipments/${newShipment.id}?addItem=true`)
  }

  async function deleteShipment(s) {
    if (!window.confirm(`Delete shipment "${s.shipment_number}"? This cannot be undone.`)) return
    await supabase.from('shipments').update({ deleted_at: new Date().toISOString() }).eq('id', s.id)
    load()
  }

  // Shipped = left the supplier. The ship date lets the Planner hand FBA shipments over to Amazon's inbound once an
  // FBA upload after that date shows them, so they are not counted twice.
  async function markShipped(s) {
    await supabase.from('shipments').update({ status: 'in_transit', actual_ship_date: s.actual_ship_date || format(new Date(), 'yyyy-MM-dd') }).eq('id', s.id)
    load()
  }

  async function cancelShipment(s) {
    if (!window.confirm(`Cancel shipment "${s.shipment_number}"?`)) return
    await supabase.from('shipments').update({ status: 'cancelled' }).eq('id', s.id)
    load()
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  let filtered = shipments.filter(s =>
    (s.shipment_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.carrier || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.supplier_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.purchase_orders?.po_number || '').toLowerCase().includes(search.toLowerCase())
  )
  filtered = filtered.filter(s => statusFilter.includes(stateOf(s)))

  const sorted = [...filtered].sort((a, b) => {
    let av, bv
    switch (sortCol) {
      case 'shipment_number': av = a.shipment_number || ''; bv = b.shipment_number || ''; break
      case 'supplier': av = a.supplier_name || ''; bv = b.supplier_name || ''; break
      case 'po': av = a.purchase_orders?.po_number || ''; bv = b.purchase_orders?.po_number || ''; break
      case 'destination': av = a.ship_to_loc?.name || a.ship_to_freeform || ''; bv = b.ship_to_loc?.name || b.ship_to_freeform || ''; break
      case 'carrier': av = a.carrier || ''; bv = b.carrier || ''; break
      case 'status': av = STATUSES.indexOf(stateOf(a)); bv = STATUSES.indexOf(stateOf(b)); break
      case 'expected_ship_date': av = a.expected_ship_date || ''; bv = b.expected_ship_date || ''; break
      case 'actual_ship_date': av = a.actual_ship_date || ''; bv = b.actual_ship_date || ''; break
      case 'expected_arrival': av = a.expected_arrival || ''; bv = b.expected_arrival || ''; break
      case 'actual_arrival': av = a.actual_arrival || ''; bv = b.actual_arrival || ''; break
      case 'ordered': av = shipmentOrdered[a.id] || 0; bv = shipmentOrdered[b.id] || 0; break
      case 'shipped': av = shipmentShipped[a.id] || 0; bv = shipmentShipped[b.id] || 0; break
      case 'received': av = shipmentReceived[a.id] || 0; bv = shipmentReceived[b.id] || 0; break
      default: av = a.created_at || ''; bv = b.created_at || ''
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  function exportShipments() {
    const header = ['Shipment #', 'Supplier', 'PO', 'Destination', 'Carrier', 'Tracking #', 'Status', 'Exp Ship', 'Act Ship', 'Exp Arrival', 'Arrived', 'Ordered', 'Shipped', 'Received']
    const rows = sorted.map(s => [
      s.shipment_number, s.supplier_name, s.purchase_orders?.po_number,
      s.is_direct_ship ? (s.ship_to_freeform || 'Direct Ship') : (s.ship_to_loc?.name || ''),
      s.carrier, s.tracking_number, DISPLAY[stateOf(s)].label,
      s.expected_ship_date, s.actual_ship_date, s.expected_arrival, s.actual_arrival,
      shipmentOrdered[s.id] || 0, shipmentShipped[s.id] || 0, shipmentReceived[s.id] || 0
    ])
    exportToCsv('shipments.csv', [header, ...rows])
  }

  function getDestination(s) {
    if (s.is_direct_ship) return <span style={{ color: 'var(--text-2)', fontStyle: 'italic' }}>{s.ship_to_freeform || 'Direct Ship'}</span>
    if (s.ship_to_loc) return s.ship_to_loc.name
    return '—'
  }

  const sortProps = { sortCol, sortDir, onSort: handleSort }

  const statusLabel =
    statusFilter.length === STATUSES.length ? 'All statuses'
    : statusFilter.length === 0 ? 'No statuses'
    : statusFilter.length === ACTIVE_STATUSES.length && ACTIVE_STATUSES.every(s => statusFilter.includes(s)) ? 'Active'
    : `${statusFilter.length} statuses`

  return (
    <div className="page">
      <div className="page-header">
        <h1>Inbound Shipments<HelpLink section="shipments" /></h1>
        <div className="page-header-actions">
          <div className="search-input-wrap"><Search size={14} /><input className="input" placeholder="Search shipments..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <div ref={statusRef} style={{ position: 'relative' }}>
            <button className="btn btn-secondary" onClick={() => setStatusOpen(o => !o)}
              style={{ minWidth: '150px', justifyContent: 'space-between' }}>
              {statusLabel}
              <ChevronDown size={14} />
            </button>
            {statusOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', padding: '6px', minWidth: '200px' }}>
                <div style={{ display: 'flex', gap: '4px', padding: '2px 4px 8px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setStatusFilter([...STATUSES])}>All</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setStatusFilter([...ACTIVE_STATUSES])}>Active</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setStatusFilter([])}>None</button>
                </div>
                {STATUSES.map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '13px', textTransform: 'capitalize' }}>
                    <input type="checkbox" checked={statusFilter.includes(s)} onChange={() => toggleStatus(s)} />
                    {DISPLAY[s].label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-secondary" onClick={exportShipments}><Download size={14} />Export CSV</button>
          <button className="btn btn-primary" onClick={openNew}><Plus size={14} />New Shipment</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : sorted.length === 0 ? <div className="empty-state"><Truck size={40} /><p>No shipments yet</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <SortTh col="shipment_number" label="Shipment #" {...sortProps} />
                    <SortTh col="supplier" label="Supplier" {...sortProps} />
                    <SortTh col="po" label="PO" {...sortProps} />
                    <SortTh col="destination" label="Destination" {...sortProps} />
                    <SortTh col="carrier" label="Carrier" {...sortProps} />
                    <SortTh col="status" label="Status" {...sortProps} />
                    <SortTh col="expected_ship_date" label="Exp Ship" {...sortProps} />
                    <SortTh col="actual_ship_date" label="Act Ship" {...sortProps} />
                    <SortTh col="expected_arrival" label="Exp Arrival" {...sortProps} />
                    <SortTh col="actual_arrival" label="Arrived" {...sortProps} />
                    <SortTh col="ordered" label="Ordered" {...sortProps} />
                    <SortTh col="shipped" label="Shipped" {...sortProps} />
                    <SortTh col="received" label="Received" {...sortProps} />
                    <th>Items</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(s => {
                    const lines = shipmentLines[s.id] || []
                    return (
                      <tr key={s.id}>
                        <td><Link to={`/shipments/${s.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: '13px', fontWeight: 500 }}>{s.shipment_number || '—'}</Link>{s.shipment_number && <CopyBtn text={s.shipment_number} />}</td>
                        <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{s.supplier_name || <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                        <td>{s.purchase_orders ? <Link to={`/purchase-orders/${s.po_id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: '12px' }}>{s.purchase_orders.po_number}</Link> : <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>}</td>
                        <td>{getDestination(s)}</td>
                        <td>{s.carrier || '—'}</td>
                        <td><span className={`badge badge-${DISPLAY[stateOf(s)].badge}`} title={stateOf(s) === 'landed' ? 'Arrival date is before the latest inventory upload for this destination' : undefined}>{DISPLAY[stateOf(s)].label}</span></td>
                        <td style={{ fontSize: '12px' }}>{s.expected_ship_date ? format(new Date(s.expected_ship_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px' }}>{s.actual_ship_date ? format(new Date(s.actual_ship_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px' }}>{s.expected_arrival ? format(new Date(s.expected_arrival + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px' }}>{s.actual_arrival ? format(new Date(s.actual_arrival + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ color: 'var(--text-2)' }}>{shipmentOrdered[s.id] || '—'}</td>
                        <td style={{ color: 'var(--text-2)' }}>{shipmentShipped[s.id] || '—'}</td>
                        <td style={{ fontWeight: 500, color: shipmentReceived[s.id] ? 'var(--success)' : 'var(--text-3)' }}>{shipmentReceived[s.id] || '—'}</td>
                        <td style={{ minWidth: '160px', maxWidth: '220px' }}>
                          {lines.length === 0 ? (
                            <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {lines.map((li, i) => (
                                <span key={i} style={{ fontSize: '11px', lineHeight: 1.3 }}>
                                  <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--accent)' }}>{li.sku || '—'}</span>
                                  {li.name && <span style={{ color: 'var(--text-2)' }}> · {li.name.length > 18 ? li.name.slice(0, 18) + '…' : li.name}</span>}
                                  <span style={{ color: 'var(--text-3)' }}> ({li.qty ?? '—'})</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            {stateOf(s) === 'planned' && (
                              <button className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} title="Mark as shipped (ship date: today unless already set)" onClick={() => markShipped(s)}>Mark shipped</button>
                            )}
                            {['planned', 'shipped'].includes(stateOf(s)) && (
                              <button className="btn btn-ghost btn-sm" title="Cancel" onClick={() => cancelShipment(s)} style={{ color: 'var(--warning)' }}>✕</button>
                            )}
                            {!isBrandAdmin && <button className="btn btn-ghost btn-sm" onClick={() => deleteShipment(s)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-lg" style={{ maxWidth: '680px' }}>
            <div className="modal-header">
              <h2>New Shipment</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-danger">{formError}</div>}

              {noPOWarning && !form.po_id && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', background: 'var(--warning-bg)', border: '1px solid rgba(240,160,80,0.3)', borderRadius: 'var(--radius)', marginBottom: '4px' }}>
                  <AlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--warning)', marginBottom: '2px' }}>No PO linked</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)' }}>This shipment has no purchase order linked. You can still create it — click Create again to confirm.</div>
                  </div>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Shipment Number *</label>
                  <input className="input" value={form.shipment_number} onChange={e => setForm(f => ({ ...f, shipment_number: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <input className="input" placeholder="e.g. Acme Manufacturing" value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Linked PO</label>
                <select className="select" value={form.po_id} onChange={e => { setForm(f => ({ ...f, po_id: e.target.value })); setNoPOWarning(false) }}>
                  <option value="">— None —</option>
                  {pos.map(p => <option key={p.id} value={p.id}>{p.po_number}</option>)}
                </select>
              </div>

              {/* Ship To */}
              <div className="form-group">
                <label className="form-label">Ship To</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', flex: 1, padding: '8px 12px', background: !form.is_direct_ship ? 'var(--accent-bg)' : 'var(--bg-3)', borderRadius: 'var(--radius)', border: `1px solid ${!form.is_direct_ship ? 'var(--accent)' : 'var(--border)'}` }}>
                    <input type="radio" checked={!form.is_direct_ship} onChange={() => setForm(f => ({ ...f, is_direct_ship: false }))} />
                    Location
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', flex: 1, padding: '8px 12px', background: form.is_direct_ship ? 'var(--accent-bg)' : 'var(--bg-3)', borderRadius: 'var(--radius)', border: `1px solid ${form.is_direct_ship ? 'var(--accent)' : 'var(--border)'}` }}>
                    <input type="radio" checked={form.is_direct_ship} onChange={() => setForm(f => ({ ...f, is_direct_ship: true }))} />
                    Direct Ship / Sample
                  </label>
                </div>
                {!form.is_direct_ship ? (
                  <select className="select" value={form.ship_to_location_id} onChange={e => setForm(f => ({ ...f, ship_to_location_id: e.target.value }))}>
                    <option value="">— Select location —</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                ) : (
                  <input className="input" placeholder="e.g. Sarah Chen - Sample, Trade Show NYC" value={form.ship_to_freeform} onChange={e => setForm(f => ({ ...f, ship_to_freeform: e.target.value }))} />
                )}
              </div>

              <div className="form-grid">
                <div className="form-group"><label className="form-label">Carrier</label><input className="input" value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))} placeholder="FedEx, UPS, DHL..." /></div>
                <div className="form-group"><label className="form-label">Tracking Number</label><input className="input" value={form.tracking_number} onChange={e => setForm(f => ({ ...f, tracking_number: e.target.value }))} /></div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Dates</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Expected Ship Date</label><input className="input" type="date" value={form.expected_ship_date} onChange={e => setForm(f => ({ ...f, expected_ship_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Actual Ship Date</label><input className="input" type="date" value={form.actual_ship_date} onChange={e => setForm(f => ({ ...f, actual_ship_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Expected Arrival</label><input className="input" type="date" value={form.expected_arrival} onChange={e => setForm(f => ({ ...f, expected_arrival: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Actual Arrival</label><input className="input" type="date" value={form.actual_arrival} onChange={e => setForm(f => ({ ...f, actual_arrival: e.target.value }))} /></div>
              </div>

              <div className="form-group" style={{ maxWidth: '50%' }}>
                <label className="form-label">Status</label>
                <select className="select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {SHIPMENT_STATUS_OPTIONS.filter(o => o.value !== 'cancelled').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !form.shipment_number}>
                {saving ? 'Creating...' : noPOWarning && !form.po_id ? 'Create Without PO' : 'Create Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
